import { FrameworkName } from './frameworks'
import { handleError } from './handleError'
import { getRegistryComponents } from './registry'
import prompts from 'prompts'

import { AddOptions } from '@commands/add'

export type PromptForNewProject = {
  projectName: string
  useSrcDir: boolean
  framework: FrameworkName
}

export const promptForNewProject = async (skipPrompts = false): Promise<PromptForNewProject> => {
  if (skipPrompts) {
    return { projectName: 'my-app', useSrcDir: true, framework: 'next-app' }
  }

  const { projectName, useSrcDir, framework } = await prompts([
    {
      type: 'text',
      name: 'projectName',
      message: `What would you like to name your project?`,
      initial: 'my-app',
      format: (value: string) => value.trim(),
      validate: (value: string) => (value.length > 128 ? `Name should be less than 128 characters.` : true),
    },
    {
      type: 'select',
      name: 'framework',
      message: 'Which framework do you want to use?',
      choices: [
        { title: 'Next.js', value: 'next-app' },
        { title: 'Vite', value: 'vite' },
      ],
      initial: 0,
    },
    {
      type: (_, values) => (values.framework === 'next-app' ? 'confirm' : null),
      name: 'useSrcDir',
      message: `Would you like to use /src directory?`,
      initial: true,
    },
  ])

  return { projectName, useSrcDir: framework === 'next-app' ? useSrcDir : true, framework }
}

/**
 * Prompts the user to select components if they were not provided via CLI.
 *
 * @param options - The parsed command options.
 * @returns A promise resolving to an array of selected component names.
 */
export async function promptForComponents(options: AddOptions): Promise<string[]> {
  try {
    const registryIndex = await getRegistryComponents()
    const componentNames = registryIndex
      .filter((component) => component.type === 'ui' || component.type === 'animated')
      .map((components) => components.name)

    if (options.all) {
      return componentNames
    }

    if (options.components?.length) {
      return options.components
    }

    const { components } = await prompts({
      type: 'multiselect',
      name: 'components',
      message: 'Which components would you like to add?',
      hint: 'Space to select. A to toggle all. Enter to submit.',
      instructions: false,
      choices: componentNames.map((component) => ({
        title: component,
        value: component,
        selected: options.components?.includes(component),
      })),
    })
    return components as string[]
  } catch (error) {
    handleError(new Error('Failed to fetch available components.'))
    return []
  }
}
